import { Button, Typography } from "@povio/ui";
import { useTranslation } from "react-i18next";

import { ShareIcon } from "@/assets/icons/general/Share";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";

interface IOSInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * iOS PWA Installation Instructions Modal
 * Shows step-by-step instructions for installing the app on iOS devices
 * iOS doesn't support the beforeinstallprompt API, so we need to show manual instructions
 */
export const IOSInstallModal = ({ isOpen, onClose }: IOSInstallModalProps) => {
  const { t } = useTranslation();

  return (
    <ModalSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t("shared.pwa.ios.modalTitle")}
      fitContent
    >
      <div className="flex flex-col gap-6">
        {/* Introduction */}
        <Typography
          variant="default"
          size="body-4"
          className="text-content-default-1"
        >
          {t("shared.pwa.ios.intro")}
        </Typography>

        {/* Step-by-step instructions */}
        <div className="flex flex-col gap-4">
          {/* Step 1 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                1
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.ios.step1Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.ios.step1Description")}
              </Typography>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                2
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.ios.step2Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.ios.step2Description")}
              </Typography>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-row gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-interactive-contained-primary-idle">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-white"
              >
                3
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <Typography
                variant="prominent-1"
                size="body-4"
                className="text-content-default-1"
              >
                {t("shared.pwa.ios.step3Title")}
              </Typography>
              <Typography
                variant="default"
                size="body-4"
                className="text-content-default-2"
              >
                {t("shared.pwa.ios.step3Description")}
              </Typography>
            </div>
          </div>
        </div>

        {/* Visual hint with share icon */}
        <div className="flex flex-col items-center gap-2 rounded-lg bg-elevation-fill-default-1 p-4">
          <Typography
            variant="prominent-1"
            size="body-4"
            className="text-content-default-2"
          >
            {t("shared.pwa.ios.lookFor")}
          </Typography>
          <div className="flex items-center justify-center rounded-lg bg-interactive-contained-primary-idle p-3">
            <ShareIcon className="h-6 w-6 text-white" />
          </div>
          <Typography
            variant="default"
            size="body-4"
            className="text-center text-content-default-3"
          >
            {t("shared.pwa.ios.shareIconHint")}
          </Typography>
        </div>

        {/* Close button */}
        <Button
          variant="contained"
          color="primary"
          size="m"
          width="fill"
          onPress={onClose}
        >
          {t("shared.pwa.ios.gotItButton")}
        </Button>
      </div>
    </ModalSheet>
  );
};
